/**
 * AI Interview Practice System - No-signup Guest Demo
 *
 * One fixed live-coding round with a 10-minute countdown, AI follow-ups and a
 * score at the end. Uses a short-lived guest token that the backend only
 * accepts for this round, so CV upload, recording, sessions, history and
 * export are deliberately absent here.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ChatDisplay from './ChatDisplay';
import CodeEditor from './CodeEditor';
import FeedbackModal from './FeedbackModal';
import VoiceRecorder from './VoiceRecorder';

const isDevelopment =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';
const WS_URL =
  import.meta.env.VITE_WS_URL ||
  (isDevelopment
    ? 'ws://localhost:5000'
    : 'wss://6kp1lphd5a.execute-api.eu-west-2.amazonaws.com/prod');
const API_URL =
  import.meta.env.VITE_API_URL ||
  (isDevelopment
    ? 'http://localhost:5000'
    : 'https://krow7dgex4.execute-api.eu-west-2.amazonaws.com');
const FULL_ACCESS_URL = '/pricing';

const DEMO_SECONDS = 10 * 60;
const GUEST_CONNECTION_ID = 'guest-demo';
// Not WebSocket.OPEN: the constant is missing when WebSocket is stubbed.
const WS_OPEN = 1;

type Phase = 'intro' | 'connecting' | 'live' | 'scoring' | 'done' | 'error';

interface Message {
  sender: 'user' | 'ai' | 'system' | 'error';
  text: string;
  timestamp: Date | string;
  isStreaming?: boolean;
}

interface Feedback {
  score?: number | null;
  summary?: string;
  strengths?: string[];
  improvements?: string[];
  problem_solving_approach_score?: number | null;
}

interface GuestSessionResponse {
  token: string;
  demo_duration_seconds: number;
  problem_title: string;
}

interface ServerMessage {
  type: string;
  text?: string;
  message?: string;
  audio_data?: string;
  feedback?: Feedback;
  reason?: string;
  message_type?: string;
  message_id?: string;
  chunk_index?: number;
  total_chunks?: number;
  chunk_data?: string;
}

interface CodeData {
  currentCode: string;
  codeHistory: string;
  output: string;
  timestamp: string;
}

const formatCountdown = (seconds: number): string => {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  return `${minutes.toString().padStart(2, '0')}:${(safe % 60)
    .toString()
    .padStart(2, '0')}`;
};

const readErrorDetail = async (response: Response): Promise<string> => {
  try {
    const body = (await response.json()) as { detail?: string };
    if (body.detail) return body.detail;
  } catch {
    // fall through to the generic message
  }
  return `Request failed (${response.status})`;
};

const FullAccessCta: React.FC = () => (
  <div className="mt-6 p-5 rounded-xl bg-gradient-to-r from-indigo-500/10 to-purple-600/10 border border-indigo-100 text-center">
    <p className="text-slate-800 font-semibold m-0">
      Want the full interviewer?
    </p>
    <p className="text-slate-500 text-sm mt-1 mb-4">
      Standard adds technical Q&amp;A and behavioural rounds from £15/month.
      Pro unlocks system design, CV-based interviews, recordings and export
      for £30/month.
    </p>
    <a
      href={FULL_ACCESS_URL}
      className="inline-block bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-lg no-underline"
    >
      Request full access
    </a>
  </div>
);

const GuestDemo: React.FC = () => {
  const [phase, setPhase] = useState<Phase>('intro');
  const [errorText, setErrorText] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [secondsLeft, setSecondsLeft] = useState<number>(DEMO_SECONDS);
  const [isAIResponding, setIsAIResponding] = useState<boolean>(false);
  const [isListening, setIsListening] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [showFeedback, setShowFeedback] = useState<boolean>(false);
  const [ws, setWs] = useState<WebSocket | null>(null);

  const phaseRef = useRef<Phase>('intro');
  const wsRef = useRef<WebSocket | null>(null);
  const tokenRef = useRef<string | null>(null);
  const deadlineRef = useRef<number>(0);
  const audioQueueRef = useRef<string[]>([]);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const voiceChunksRef = useRef<Record<string, (string | null)[]>>({});
  const handleServerMessageRef = useRef<(data: ServerMessage) => void>(
    () => undefined
  );

  const updatePhase = useCallback((next: Phase) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  const addMessage = useCallback(
    (sender: Message['sender'], text: string) => {
      setMessages((prev) => [...prev, { sender, text, timestamp: new Date() }]);
    },
    []
  );

  const stopAudio = useCallback(() => {
    audioQueueRef.current = [];
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
  }, []);

  const playNextAudio = useCallback(() => {
    if (currentAudioRef.current || audioQueueRef.current.length === 0) return;
    const next = audioQueueRef.current.shift() as string;
    const audio = new Audio(`data:audio/mpeg;base64,${next}`);
    currentAudioRef.current = audio;
    const advance = (): void => {
      currentAudioRef.current = null;
      playNextAudio();
    };
    audio.onended = advance;
    audio.onerror = advance;
    audio.play().catch(advance);
  }, []);

  const queueAudio = useCallback(
    (base64: string) => {
      if (phaseRef.current !== 'live') return;
      audioQueueRef.current.push(base64);
      playNextAudio();
    },
    [playNextAudio]
  );

  const closeSocket = useCallback(() => {
    const socket = wsRef.current;
    wsRef.current = null;
    setWs(null);
    if (!socket) return;
    socket.onclose = null;
    socket.onmessage = null;
    socket.onerror = null;
    try {
      if (socket.readyState === WS_OPEN) {
        socket.send(JSON.stringify({ type: 'end_interview' }));
      }
      socket.close(1000, 'Demo finished');
    } catch {
      // socket already closed
    }
  }, []);

  const finishRound = useCallback(() => {
    if (phaseRef.current !== 'live') return;
    updatePhase('scoring');
    setIsListening(false);
    setIsAIResponding(false);
    stopAudio();

    const socket = wsRef.current;
    if (!socket || socket.readyState !== WS_OPEN) {
      setErrorText('The connection dropped before your score was ready.');
      updatePhase('done');
      return;
    }
    setFeedback(null);
    setShowFeedback(true);
    socket.send(JSON.stringify({ type: 'request_feedback' }));
  }, [stopAudio, updatePhase]);

  const handleServerMessage = (data: ServerMessage): void => {
    switch (data.type) {
      case 'welcome':
        if (data.message) addMessage('system', data.message);
        break;
      case 'response_chunk':
        if (!data.text) break;
        setIsAIResponding(true);
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.sender === 'ai' && last.isStreaming) {
            return [
              ...prev.slice(0, -1),
              { ...last, text: last.text + data.text },
            ];
          }
          return [
            ...prev,
            {
              sender: 'ai',
              text: data.text as string,
              timestamp: new Date(),
              isStreaming: true,
            },
          ];
        });
        break;
      case 'response_complete':
        setIsAIResponding(false);
        setMessages((prev) =>
          prev.map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m))
        );
        break;
      case 'voice_response':
        if (data.audio_data) queueAudio(data.audio_data);
        break;
      case 'chunk': {
        if (data.message_type !== 'voice_response' || !data.message_id) break;
        const total = data.total_chunks ?? 0;
        const parts =
          voiceChunksRef.current[data.message_id] ??
          new Array<string | null>(total).fill(null);
        parts[data.chunk_index ?? 0] = data.chunk_data ?? '';
        voiceChunksRef.current[data.message_id] = parts;
        if (parts.every((p) => p !== null)) {
          delete voiceChunksRef.current[data.message_id];
          queueAudio(parts.join(''));
        }
        break;
      }
      case 'guest_limit':
        if (data.reason === 'session_expired') {
          setErrorText(data.message || 'This demo session has expired.');
          stopAudio();
          setShowFeedback(false);
          updatePhase('done');
          closeSocket();
        } else {
          finishRound();
        }
        break;
      case 'error':
        setIsAIResponding(false);
        if (data.message) addMessage('error', data.message);
        break;
      case 'feedback_response':
        setFeedback(data.feedback ?? { score: 0, summary: 'No feedback.' });
        updatePhase('done');
        closeSocket();
        break;
      case 'feedback_error':
        setShowFeedback(false);
        setErrorText(data.message || 'We could not generate your score.');
        updatePhase('done');
        closeSocket();
        break;
      default:
        break;
    }
  };
  handleServerMessageRef.current = handleServerMessage;

  const startDemo = async (): Promise<void> => {
    updatePhase('connecting');
    setErrorText(null);
    setMessages([]);
    setFeedback(null);

    let session: GuestSessionResponse;
    try {
      const response = await fetch(`${API_URL}/guest/session`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error(await readErrorDetail(response));
      session = (await response.json()) as GuestSessionResponse;
    } catch (error) {
      setErrorText(
        error instanceof Error ? error.message : 'Could not start the demo.'
      );
      updatePhase('error');
      return;
    }

    tokenRef.current = session.token;
    const token = encodeURIComponent(session.token);
    const socketUrl = isDevelopment
      ? `${WS_URL}/ws/guest-${Date.now().toString(36)}${Math.random()
          .toString(36)
          .slice(2)}?token=${token}`
      : `${WS_URL}?token=${token}`;
    const socket = new WebSocket(socketUrl);
    wsRef.current = socket;

    socket.onopen = (): void => {
      setWs(socket);
      const duration = session.demo_duration_seconds || DEMO_SECONDS;
      deadlineRef.current = Date.now() + duration * 1000;
      setSecondsLeft(duration);
      updatePhase('live');
      socket.send(
        JSON.stringify({ type: 'start_interview', interview_type: 'coding' })
      );
    };
    socket.onmessage = (event: MessageEvent): void => {
      try {
        handleServerMessageRef.current(
          JSON.parse(event.data as string) as ServerMessage
        );
      } catch (error) {
        console.error('Invalid demo message', error);
      }
    };
    socket.onerror = (): void => {
      if (phaseRef.current === 'connecting') {
        setErrorText('Could not connect to the AI interviewer.');
        updatePhase('error');
      }
    };
    socket.onclose = (): void => {
      wsRef.current = null;
      setWs(null);
      if (phaseRef.current === 'live' || phaseRef.current === 'connecting') {
        stopAudio();
        setErrorText('The connection to the AI interviewer was lost.');
        updatePhase('error');
      } else if (phaseRef.current === 'scoring') {
        setShowFeedback(false);
        setErrorText('The connection dropped before your score was ready.');
        updatePhase('done');
      }
    };
  };

  useEffect(() => {
    if (phase !== 'live') return undefined;
    const interval = window.setInterval(() => {
      const remaining = Math.max(
        0,
        Math.ceil((deadlineRef.current - Date.now()) / 1000)
      );
      setSecondsLeft(remaining);
      if (remaining === 0) finishRound();
    }, 1000);
    return () => window.clearInterval(interval);
  }, [phase, finishRound]);

  useEffect(
    () => () => {
      stopAudio();
      closeSocket();
    },
    [stopAudio, closeSocket]
  );

  const sendText = (text: string): void => {
    const socket = wsRef.current;
    if (phaseRef.current !== 'live' || !socket || socket.readyState !== WS_OPEN)
      return;
    stopAudio();
    addMessage('user', text);
    setIsAIResponding(true);
    socket.send(JSON.stringify({ type: 'text', text }));
  };

  const handleVoiceAnswer = async (audioBase64: string): Promise<void> => {
    if (phaseRef.current !== 'live' || !audioBase64) return;
    try {
      const response = await fetch(`${API_URL}/guest/transcribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenRef.current}`,
        },
        body: JSON.stringify({ audio_data: audioBase64 }),
      });
      if (!response.ok) throw new Error(await readErrorDetail(response));
      const { transcript } = (await response.json()) as { transcript: string };
      if (transcript && transcript.trim()) sendText(transcript.trim());
    } catch (error) {
      addMessage(
        'error',
        error instanceof Error ? error.message : 'Voice input failed.'
      );
    }
  };

  const handleCodeSubmit = (codeData: CodeData): void => {
    stopAudio();
    addMessage(
      'user',
      `Here's my code:\n\`\`\`javascript\n${codeData.currentCode}\n\`\`\`${
        codeData.output ? `\n\nOutput:\n${codeData.output}` : ''
      }`
    );
    setIsAIResponding(true);
  };

  if (phase === 'intro' || phase === 'connecting' || phase === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 p-5">
        <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-2xl border border-slate-200 w-full max-w-lg">
          <h1 className="font-mono text-2xl sm:text-3xl font-bold text-slate-800 mb-2 tracking-tight text-center">
            10-minute coding demo
          </h1>
          <p className="text-slate-500 text-sm text-center mb-6">
            No signup needed
          </p>
          <ul className="text-slate-700 text-sm space-y-2 mb-6 pl-5 list-disc">
            <li>One live-coding problem with an AI interviewer.</li>
            <li>Write and run JavaScript in the editor, then send it for review.</li>
            <li>Answer follow-ups by typing or speaking.</li>
            <li>You get a score and feedback when the 10 minutes are up.</li>
          </ul>
          <p className="text-slate-400 text-xs mb-6">
            We don&apos;t ask for any personal details. Demo transcripts are
            deleted automatically after 24 hours.
          </p>
          {errorText && (
            <div
              role="alert"
              className="bg-red-50 border border-red-300 rounded-lg p-3 px-4 text-red-700 text-sm mb-4"
            >
              {errorText}
            </div>
          )}
          <button
            onClick={startDemo}
            disabled={phase === 'connecting'}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg px-6 py-3.5 text-base font-semibold cursor-pointer transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed hover:from-indigo-600 hover:to-purple-700"
          >
            {phase === 'connecting'
              ? 'Starting…'
              : phase === 'error'
                ? 'Try again'
                : 'Start demo'}
          </button>
          <div className="mt-4 text-center text-sm">
            <a href="/" className="text-indigo-600 hover:underline">
              Have an account? Sign in
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'done' && !showFeedback) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 p-5">
        <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-2xl border border-slate-200 w-full max-w-lg text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-2">
            Demo complete
          </h1>
          {feedback && feedback.score !== null && feedback.score !== undefined && (
            <p className="text-slate-700 text-lg m-0">
              Your score: <strong>{feedback.score}/100</strong>
            </p>
          )}
          {feedback?.summary && (
            <p className="text-slate-500 text-sm mt-3">{feedback.summary}</p>
          )}
          {errorText && (
            <p role="alert" className="text-red-600 text-sm mt-3">
              {errorText}
            </p>
          )}
          <FullAccessCta />
          <a
            href="/"
            className="inline-block mt-4 text-sm text-indigo-600 hover:underline"
          >
            Back to sign in
          </a>
        </div>
      </div>
    );
  }

  const isLive = phase === 'live';
  return (
    <div className="flex flex-col h-screen min-h-0 overflow-x-hidden bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 text-slate-800">
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-3 sm:px-6 py-2 sm:py-3 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-slate-800 text-sm sm:text-lg font-semibold m-0 truncate">
              Live Coding Demo
            </h2>
            <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs font-semibold">
              Guest
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span
              aria-label="Time remaining"
              className={`px-3 py-1 rounded font-mono text-sm font-bold ${
                secondsLeft <= 60
                  ? 'bg-red-100 text-red-600 animate-pulse'
                  : 'bg-emerald-100 text-emerald-700'
              }`}
            >
              ⏱️ {formatCountdown(secondsLeft)}
            </span>
            <button
              onClick={finishRound}
              disabled={!isLive}
              className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-semibold whitespace-nowrap disabled:opacity-50"
            >
              End &amp; get score
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden gap-3 p-3 sm:p-4 lg:p-6">
        <div className="flex-1 lg:flex-[2] flex flex-col gap-3 min-w-0 overflow-hidden">
          <CodeEditor
            onCodeSubmit={handleCodeSubmit}
            connectionId={GUEST_CONNECTION_ID}
            ws={ws}
          />
          <div className="flex-shrink-0">
            <VoiceRecorder
              isRecording={isListening}
              isMuted={false}
              onMuteToggle={() => setIsListening((prev) => isLive && !prev)}
              onAudioData={handleVoiceAnswer}
              connectionId={GUEST_CONNECTION_ID}
              interviewType="coding"
            />
          </div>
        </div>
        <div className="flex-1 min-w-0 lg:flex-[1] lg:min-w-[280px] lg:max-w-[500px] bg-white border border-slate-200 rounded-xl shadow-md flex flex-col overflow-hidden">
          <ChatDisplay
            messages={messages}
            onSendMessage={sendText}
            isAIResponding={isAIResponding}
            onInterrupt={stopAudio}
          />
        </div>
      </div>

      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => {
          if (phaseRef.current === 'done') setShowFeedback(false);
        }}
        feedback={feedback}
        messages={messages}
        hideExport
        footer={<FullAccessCta />}
      />
    </div>
  );
};

export default GuestDemo;
